import React, { useMemo, useState } from "react";
import {
  CitationsFragment,
  DiseaseReferenceDataCitationsDvData,
  QualifiedFeature,
} from "fe-common/graphql";
import { ResourceCountDisplay } from "./ResourceCountDisplay";
import {
  List,
  ListItem,
  styled,
  ListItemText,
  FormControlLabel,
  Checkbox,
  ListItemIcon,
} from "@mui/material";
import { ArticleLink } from "./ArticlesLink";
import { featureToLines } from "fe-common/logic";
import { KahunPrimaryTypography } from "../styles/typography";
import { KahunColorMap } from "fe-common/ui/assets/colorMap";
import LaunchIcon from "@mui/icons-material/Launch";
const MobileView = "xs";

export interface CitationsProps {
  citations: CitationsFragment;
  citationQfsData?: DiseaseReferenceDataCitationsDvData[];
  onFindingSelection?: (cohort) => void;
  onClick?: (string) => void;
}

const CitationsView = styled("div")(({ theme }) => ({
  [theme.breakpoints.down(MobileView)]: {
    paddingRight: "12px",
    paddingLeft: "12px",
  },
  paddingRight: "16px",
  paddingLeft: "16px",
}));

const ListItemStyled = styled(ListItem)(({ theme }) => ({
  [theme.breakpoints.down(MobileView)]: {
    marginBottom: "2vh",

    backgroundColor: "white",
  },

  flexDirection: "column",
  alignItems: "start",
  padding: "0",
  marginBottom: "10px",
}));

const ArticleTitle = styled("span")({
  fontSize: "14px",
  color: KahunColorMap.Primary,
  fontWeight: 600,
});

const ArticleDetails = styled("span")({
  fontSize: "14px",
  color: KahunColorMap.DefaultLight,
});

const ArticleLinkLabel = styled("span")({
  color: KahunColorMap.BlueReferences,
});

const FeaturesListItem = styled(ListItem)({
  display: "flex",
  flexWrap: "wrap",
});

const FeatureItemText = styled(KahunPrimaryTypography)({
  "&:hover": {
    cursor: "pointer",
    color: KahunColorMap.Accent,
  },
});

const CitationItem = styled("div")({
  display: "flex",
});

const CitationEditorLink = styled(ListItemIcon)({
  "&:hover": {
    cursor: "pointer",
    color: KahunColorMap.Accent,
  },
});

const CitationFeaturesList = ({
  featureType,
  onFindingSelection,
  list,
}: {
  featureType: "subtypes" | "findings";
  list: { id: string; qfs: QualifiedFeature[] }[];
  onFindingSelection: (cohort) => void;
}) => {
  const [showFindings, setShowFindings] = useState(false);

  if (!list || !list.length) return null;

  return (
    <>
      <FormControlLabel
        label={`show ${featureType}`}
        control={
          <Checkbox
            checked={showFindings}
            onChange={(e) => setShowFindings(e.target.checked)}
            inputProps={{ "aria-label": "primary checkbox" }}
          />
        }
      />
      {showFindings && (
        <>
          <ListItemText
            primary={
              <KahunPrimaryTypography>{`${featureType}:`}</KahunPrimaryTypography>
            }
          />
          <FeaturesListItem dense>
            {list.map((f) => (
              <FeatureItemText
                onClick={() => onFindingSelection && onFindingSelection(f)}
                key={f.id}
              >{`| ${featureToLines(f.qfs[0])} |`}</FeatureItemText>
            ))}
          </FeaturesListItem>
        </>
      )}
    </>
  );
};

export const Citations = ({
  citations,
  citationQfsData,
  onFindingSelection,
  onClick,
}: CitationsProps) => {
  const citationsQfsMap: {
    [id: string]: DiseaseReferenceDataCitationsDvData;
  } = useMemo(() => {
    return citationQfsData?.reduce((acc, c) => {
      acc[c.citationId] = c;
      return acc;
    }, {});
  }, [citationQfsData]);

  let sortedCitation = useMemo(() => {
    if (!citations?.citationsData) return null;

    let citationsObj = citations.citationsData;

    //In case we got a mapping of specific citations with subtypes and finding use that
    if (citationQfsData) {
      const ids = new Set([...citationQfsData.map((c) => c.citationId)]);
      citationsObj = citations.citationsData
        .filter((c) => ids.has(c.id))
        .sort(
          (a, b) => citationsQfsMap[b.id].count - citationsQfsMap[a.id].count,
        );
    }
    return citationsObj;
  }, [citations, citationQfsData, citationsQfsMap]);

  let count = sortedCitation?.length;

  if (!citations) return null;

  return (
    <CitationsView>
      <ResourceCountDisplay
        className={"resource-count-display"}
        articlesCount={count || 0}
        fullTitle={"ARTICLES"}
      />
      <List>
        {sortedCitation?.map((c, idx) => (
          <CitationItem key={c.id + idx}>
            <ListItemStyled dense disableGutters={true}>
              <ListItemText
                primary={<ArticleTitle>{c.title}</ArticleTitle>}
                secondary={<ArticleDetails>{c.vancouver}</ArticleDetails>}
              />
              <ArticleLink link={c.url} hideUnderline={true}>
                <ArticleLinkLabel>Read More</ArticleLinkLabel>
              </ArticleLink>
              <CitationFeaturesList
                list={
                  citationsQfsMap &&
                  citationsQfsMap[c.id] &&
                  citationsQfsMap[c.id]["findings"]
                }
                featureType={"findings"}
                onFindingSelection={onFindingSelection}
              />
              <CitationFeaturesList
                list={
                  citationsQfsMap &&
                  citationsQfsMap[c.id] &&
                  citationsQfsMap[c.id]["subtypes"]
                }
                featureType={"subtypes"}
                onFindingSelection={onFindingSelection}
              />
            </ListItemStyled>
            {onClick && (
              <CitationEditorLink
                onClick={(e) => {
                  e.stopPropagation();
                  onClick && onClick(c.kahunId);
                }}
              >
                <LaunchIcon />
              </CitationEditorLink>
            )}
          </CitationItem>
        ))}
      </List>
    </CitationsView>
  );
};
